import type { FC } from 'react';
import { StreamAPIWrapper } from './StreamAPI.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import EmbedYoutube from '../../utils/EmbedYoutube/EmbedYoutube';
import playSubscribe from './../../../resources/videoTutorial/play-subscribe.png';
import { Button } from 'reactstrap';

interface StreamAPIProps { }

const StreamAPI: FC<StreamAPIProps> = () => (
   <StreamAPIWrapper>
      <header className="entry-header ">
         <h1 className="entry-title" itemProp="headline">Stream API</h1>
      </header>
      <br/>
      <div className="entry-content clear" itemProp="text" style={{ height: 'auto !important' }}>
         <p className="text-align-justify">Stream API introduced in Java 8 is used to process collections of objects. A stream is a sequence of objects that supports various methods to a pipeline to produce the desired result.<br /></p>
         <br/>
         <h4  className="text-align-justify"><strong>Features </strong></h4>
         <ul  className="text-align-justify">
            <li>One cannot consider the stream as a data structure. Instead, it accepts input from the Collections, Arrays, or I/O channels.</li>
            <li>It doesn’t manipulate the original data structure; it only provides the result generated by pipelined methods.</li>
            <li>A stream consists of a set of elements in a sequential manner of a specific type.</li>
            <li>It supports aggregate operations. For instance, filter, map, limit, reduce, find, match, and so on.</li>
            <li>Unlike Collections, the iterations are performed internally by stream operations over the source elements provided. However, in collections, explicit iteration is mandatory.</li>
            <li>It does not store elements. </li>
            <li>Stream evaluates the code only when necessary, as it is lazy.</li>
            <br/>
            <li><strong>A stream mainly consists of three things :</strong>
               <ul>
                  <li>a source</li>
                  <li>zero or more intermediate methods combined (pipelined)</li>
                  <li>a terminal method that processes the objects obtained from the source.</li>
               </ul>
            </li>
            <br/>
            <li>To pipeline the result, most of the stream operations return the stream itself. These operations are intermediate. The function of intermediate operations is to take input, process them, and return output to the target.</li>
            <li>A terminal operation is the collect() method present at the end of the pipelining operation to mark the end of the stream.</li>
            <li>Collectors can combine the result of processing on the elements of a stream. Collectors can return a list or a string.</li>
            <li>However, the operations that are performed on a stream does not modify the source of the stream. </li>
         </ul>
         <br />

         <div className="epyt-gallery" data-currpage="1">
            <EmbedYoutube videoType="video" videoList={["BcQPtriMT5g"]} />
            <div className="epyt-gallery-subscribe">
               <a className="epyt-gallery-subbutton" href="https://youtube.com/channel/UCpIueiXyq5nhTJTDuw2DQnA?sub_confirmation=1">
                  <img className="lazy-loaded" decoding="async" alt="subscribe" src={playSubscribe} data-lazy-type="image" />
                  Subscribe
               </a>
            </div>
         </div>

         <h4 className="text-align-justify"><strong>Operations Performed on Stream </strong></h4>
         <br/>
         <ul className="text-align-justify">
            <li><strong>Intermediate Operations</strong>
               <ul>
                  <li><strong>map </strong>
                     <p>The map method returns a stream consisting of the results of applying the given function to the elements of this stream.</p>
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'list.stream().map(function).collect(Collectors.toList());'
                        }
                     </SyntaxHighlighter>
                  </li>
                  <li><strong>filter</strong>
                     <p>The filter method select the elements as per the Predicate passed as argument.</p>
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'list.stream().filter(predicate).collect(Collectors.toList());'
                        }
                     </SyntaxHighlighter>
                  </li>
                  <li><strong>sorted</strong>
                     <p>The sorted method sort the stream.</p>
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'list.stream().sorted().collect(Collectors.toList());'
                        }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
            <br/>
            <li><strong>Terminal Operations </strong>
               <ul>
                  <li><strong>collect</strong>
                     <p>The collect method returns the result of the intermediate operations performed on the stream.</p>
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'list.stream().map(function).collect(Collectors.toSet());'
                        }
                     </SyntaxHighlighter>
                  </li>
                  <li><strong>forEach </strong>
                     <p>The forEach method iterates through every element of the stream.</p>
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'list.stream().map(function).forEach(action);'
                        }
                     </SyntaxHighlighter>
                  </li>
                  <li><strong>reduce </strong>
                     <p>The reduce method reduce the elements of a stream to a single value. It takes a BinaryOperator as a parameter.</p>
                     <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                        {
                           'list.stream().filter(predicate).reduce(binaryOperator);'
                        }
                     </SyntaxHighlighter>
                  </li>
               </ul>
            </li>
         </ul>
         <br />
         <Button className="programs"><a href="/stream-api-programs">Want to explore Stream API Programs? Click here!</a></Button>
         <br/>
         <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
            {
               'class StreamAPIExample {\n'+
               '  public static void main(String args[]) {\n'+
               '    List <Integer> numbers = Arrays.asList(2, 3, 4, 5);\n'+
               '\n'+
               '    List <String> names = Arrays.asList("System", "Operator", "StreamAPI");\n'+
               '\n'+
               '    List <Integer> square = numbers.stream().map(x -> x * x).collect(Collectors.toList());\n'+
               '\n'+
               '    List <String> result = names.stream().filter(s -> s.startsWith("S")).collect(Collectors.toList());\n'+
               '\n'+
               '    List <String> show = names.stream().sorted().collect(Collectors.toList());\n'+
               '\n'+
               '    Set <Integer> squareSet = numbers.stream().map(x -> x * x).collect(Collectors.toSet());\n'+
               '\n'+
               '    numbers.stream().map(x -> x * x).forEach(y -> System.out.println(y));\n'+
               '\n'+
               '    int even = numbers.stream().filter(x -> x % 2 == 0).reduce(0, (ans, i) -> ans + i);\n'+
               '  }\n'+
               '}'
            }
         </SyntaxHighlighter>
      </div>
   </StreamAPIWrapper>
);

export default StreamAPI;
